import { proxy, Remote, wrap } from 'comlink';
import { Observer, Subject } from 'rxjs';
import { OPERATION } from '../../des.dto';
import { IdbIssueMedia } from '../../issue/issue.dto';
import { WarehouseSyncStepContextEvent, WarehouseSyncStepTask, WarehouseSyncStepTaskContext } from '../executors';
import { WarehouseIssuesMediasFilesStepsWorker } from '../workers/issues-medias-files.steps.worker';
import { WarehouseSyncAbstractMediaCleanupSteps } from './abstract-media-cleanup.steps';
import { executeWorker, WarehouseSyncStepWorkerFactory } from './steps-utils';
import { PictureSyncRiskFilter } from '@damofire/data-stas';
import { deleteFSFile, HttpUtils, IndexedService, ISSUE_MEDIAS_STORE, readFSFile, writeFSFile, logger } from '@damofire/data-commons';

class WarehouseIssuesMediasFilesSyncWorkerFactory implements WarehouseSyncStepWorkerFactory<WarehouseIssuesMediasFilesStepsWorker> {
  async createWorker(environment: any): Promise<[Worker, WarehouseIssuesMediasFilesStepsWorker | Remote<WarehouseIssuesMediasFilesStepsWorker>]> {
    if (environment.appConfig.watson.useWebworkers) {
      const warehouseWorker = new Worker(new URL('../workers/issues-medias-files.steps.worker', import.meta.url), { type: 'module' });
      const warehouseWorkerProxy = wrap<typeof WarehouseIssuesMediasFilesStepsWorker>(warehouseWorker);
      const workerService = await new warehouseWorkerProxy();
      return [warehouseWorker, workerService];
    }

    return [null, new WarehouseIssuesMediasFilesStepsWorker()];
  }
}

export class WarehousePushIssuesMediasFilesSyncStepTask implements WarehouseSyncStepTask {
  constructor(
    private readonly environment: any,
    private readonly idbService: IndexedService,
  ) {}

  name(): string {
    return `PUSH_IMF_TASK`;
  }

  async execute(ctx: WarehouseSyncStepTaskContext, listener: Observer<WarehouseSyncStepContextEvent>, taskBreaker: Subject<boolean>): Promise<void> {
    // Awaited before the task ends, so the tasks after it only see uploads that have finished.
    const uploads: Promise<void>[] = [];

    const itemsProcessedCallback = (change: { data: IdbIssueMedia; operation: OPERATION }, buffer: ArrayBuffer): void => {
      logger.info('Issue media operation received', { category: 'sync', operation: change.operation });
      const { operation, data } = change;
      const { _id, division, bytesPath, filename, annotations } = data;

      switch (operation) {
        case OPERATION.ADD:
          logger.info('Pushing issue media to server', { category: 'sync' });
          uploads.push(
            readFSFile(bytesPath)
              .then(async (bytes) => {
                await HttpUtils.post<void>(this.environment.appConfig.fns, 'ww-issueMediaCreate', {
                  data: {
                    ...data,
                    filename,
                    image: bytes.replace(/data:image\/[a-zA-Z]+;base64,/, ''),
                    annotations,
                  },
                });
                await this.idbService.delete(`${ISSUE_MEDIAS_STORE}-operations`, [division._id, _id, operation]);
              })
              .catch((error) => logger.error('Could not push issue media', { category: 'sync', id: _id, error })),
          );
          break;
        case OPERATION.DELETE:
          logger.info('Issue media DELETE operation received', { category: 'sync' });
          break;
        case OPERATION.UPDATE:
          logger.info('Issue media UPDATE operation received', { category: 'sync' });
          break;
        default:
      }
    };

    logger.info('Executing push issues-medias files sync task', { category: 'sync' });
    try {
      await executeWorker(ctx, this.environment, listener, new WarehouseIssuesMediasFilesSyncWorkerFactory(), taskBreaker, itemsProcessedCallback);
    } finally {
      await Promise.all(uploads);
    }
  }
}

export class WarehouseDownloadIssuesMediasFilesSyncStepTask implements WarehouseSyncStepTask {
  constructor(
    private readonly environment: any,
    private readonly minRiskLevelFilter: PictureSyncRiskFilter[] | null = null,
  ) {}

  name(): string {
    return `DOWN_IMF_TASK`;
  }

  async execute(ctx: WarehouseSyncStepTaskContext, listener: Observer<WarehouseSyncStepContextEvent>, taskBreaker: Subject<boolean>): Promise<void> {
    logger.info('Executing download issues-medias files sync task', { category: 'sync' });
    ctx.picturesRiskLevelFilter = this.minRiskLevelFilter;

    const itemsProcessedCallback = (change: { data: IdbIssueMedia; operation: OPERATION }, buffer: ArrayBuffer): void => {
      try {
        // Use the chunk-based approach:
        const base64Data = arrayBufferToBase64(buffer);
        const imageUrl = `data:${change.data.format};base64,${base64Data}`;

        if (change.data.thumbBytesPath) {
          writeFSFile(change.data.thumbBytesPath, imageUrl).catch(console.error);
        } else if (change.data.bytesPath) {
          writeFSFile(change.data.bytesPath, imageUrl).catch(console.error);
        } else {
          logger.error('Issue media missing bytes path', { category: 'sync', id: change.data._id, issueId: change.data.issue?._id });
        }
      } catch (e) {
        logger.error('Error processing issue media', { category: 'sync', id: change?.data?._id, error: e });
      }
    };

    await executeWorker(ctx, this.environment, listener, new WarehouseIssuesMediasFilesSyncWorkerFactory(), taskBreaker, itemsProcessedCallback);
  }
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000; // 32KB

  for (let i = 0; i < bytes.length; i += chunkSize) {
    // Grab a chunk of bytes at a time
    const chunk = bytes.subarray(i, i + chunkSize);
    // Convert that chunk into an array so the spread operator works neatly in TS
    binary += String.fromCharCode(...Array.from(chunk));
  }

  return btoa(binary);
}

export class WarehouseCleanIssuesMediasFilesSyncStepTask extends WarehouseSyncAbstractMediaCleanupSteps<IdbIssueMedia> implements WarehouseSyncStepTask {
  constructor(private readonly environment: any) {
    super();
  }

  name(): string {
    return `CLEAN_IMF_TASK`;
  }

  protected buildFilePaths(media: IdbIssueMedia): string[] {
    return [`/${media.division?._id}/medias/${media.thumb}`, `/${media.division?._id}/medias/${media.web}`];
  }

  async execute(ctx: WarehouseSyncStepTaskContext, listener: Observer<WarehouseSyncStepContextEvent>, taskBreaker: Subject<boolean>): Promise<void> {
    logger.info('Executing clean issues-medias files sync task', { category: 'sync' });

    const deleteFiles = proxy((change: { data: IdbIssueMedia; operation: OPERATION }, buffer: ArrayBuffer): void => {
      const paths = this.buildFilePaths(change.data);
      if (paths && paths?.length) {
        Promise.all(paths.map((path) => deleteFSFile(path))).catch(console.error);
      }
    });

    await executeWorker(ctx, this.environment, listener, new WarehouseIssuesMediasFilesSyncWorkerFactory(), taskBreaker, deleteFiles);
  }
}
